// src/components/files/Visionneuse.tsx — SCE Issues
// Visionneuse des photos d'une fiche (09/10), maquette validée :
// https://claude.ai/artifact/DcEcrXUbuMHMLCAfPxV5TJ
//
// Toutes les photos de la fiche défilent, celles du problème puis celles de
// l'activité (utils/rattachementPhotos). Gestes, tous à la main, sans
// bibliothèque :
//  - glisser à gauche ou à droite : photo suivante ou précédente ;
//  - pincer, ou toucher deux fois : zoom (jusqu'à × 5), puis glisser pour se
//    déplacer dans la photo ; molette sur PC ;
//  - toucher le fond, à côté de la photo : fermer. Photo zoomée : le même
//    geste la remet à sa taille (sinon on fermerait en voulant dézoomer).
// Un PDF s'y lit page après page (LecteurPdf, 12/10) : il défile et se zoome avec
// ses propres boutons ; on en sort par les flèches, les vignettes ou la croix.
// Une photo d'un commentaire montre le commentaire, avec un lien vers lui
// dans l'activité. Un toucher sur la photo masque en-tête et légende. Une photo annotée s'ouvre sur sa version annotée ; un
// sélecteur ramène à l'original.
//
// Transitions (retour du 09/10) : un carrousel. Les photos voisines sont déjà
// posées de part et d'autre (et chargées) ; la suivante glisse avec le doigt,
// un geste lancé suffit, et la piste finit sa course en douceur. Aux bouts,
// la photo résiste. Saut de plusieurs photos (vignettes) : fondu.
//
// Sur PC (grand écran, souris) : la photo prend toute la hauteur, légende et
// vignettes passent dans un panneau à droite, qui reste visible pendant le zoom.
//
// Netteté (22/10) : l'aperçu de SharePoint (800 px, JPEG) s'affiche tout de
// suite, puis l'ORIGINAL le remplace dès qu'il est lu — par Graph, en blob :
// la lecture qui marche aussi sur iPhone. La photo affichée d'abord, puis ses
// voisines ; gardés le temps de la visionneuse. Une photo réduite par l'aperçu
// occupe d'emblée toute la place : l'original arrive sans saut.
//
// Les flèches du clavier restent à la visionneuse tant qu'elle est ouverte :
// la fiche en surimpression, dessous, s'en sert pour changer de problème.

import { useEffect, useRef, useState, type PointerEvent as PointerEventReact, type WheelEvent } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText, PenLine, X } from 'lucide-react'
import { ImageAvecRepli, Vignette } from '@/components/files/PieceJointeGallery'
import { LecteurPdf } from '@/components/files/LecteurPdf'
import { COTE_APERCU, connexionLente, lireContenu, type PieceJointe } from '@/api/files.api'
import { estTexteAutomatique, nomAffiche, type PhotoFiche } from '@/utils/rattachementPhotos'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { useMediaQuery } from '@/hooks/useMediaQuery'

const ZOOM_MAX = 5
const ZOOM_DOUBLE_TOUCHE = 2.5
/** Au-delà, un toucher devient un glissement. */
const SEUIL_MOUVEMENT_PX = 8
const SEUIL_GLISSEMENT_PX = 60
const DELAI_DOUBLE_TOUCHE_MS = 280
/** Écart entre deux photos du carrousel. */
const ECART_PX = 24
const DUREE_GLISSEMENT_MS = 300
/** Un geste lancé plus vite que ça (px/ms) change de photo, même court. */
const VITESSE_LANCER = 0.45

interface Vue {
  s: number
  tx: number
  ty: number
}

const VUE_ENTIERE: Vue = { s: 1, tx: 0, ty: 0 }

/** PC : écran large et assez haut, à la souris. */
export const ECRAN_PC = '(min-width: 1000px) and (min-height: 560px) and (hover: hover)'

interface Geste {
  mode: 'glisse' | 'pan' | 'pince'
  x0: number
  y0: number
  vue0: Vue
  dist0: number
  milieu0: { x: number; y: number }
  bouge: boolean
  surMedia: boolean
  t0: number
}

/** « 9 oct. 09:39 » : court, la légende tient sur une ligne. */
function quand(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const jour = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return `${jour} ${heure}`
}

function borner(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v))
}

export function Visionneuse({
  photos,
  index,
  onIndex,
  onFermer,
  onVoirDansActivite,
  onAnnoter,
}: {
  photos: PhotoFiche[]
  index: number
  onIndex: (i: number) => void
  onFermer: () => void
  /** Ferme la visionneuse et amène l'événement à l'écran. */
  onVoirDansActivite?: (commentaireId: string) => void
  /** Annoter la version affichée ; absent : pas de bouton (problème terminé). */
  onAnnoter?: (photo: PhotoFiche, affichee: PieceJointe) => void
}): JSX.Element | null {
  const photo = photos[index]
  const [vue, setVue] = useState<Vue>(VUE_ENTIERE)
  // Carrousel : décalage de la piste, animé ou non, et fondu des sauts.
  const [decalage, setDecalage] = useState(0)
  const [animePiste, setAnimePiste] = useState(false)
  const [apparition, setApparition] = useState(false)
  const finGlissement = useRef<number | null>(null)
  const [anime, setAnime] = useState(false)
  const [originale, setOriginale] = useState(false)
  // Un toucher sur la photo masque en-tête et légende : la photo seule.
  const [interfaceVisible, setInterfaceVisible] = useState(true)
  const pc = useMediaQuery(ECRAN_PC)
  const scene = useRef<HTMLDivElement>(null)
  const pointeurs = useRef(new Map<number, { x: number; y: number }>())
  const geste = useRef<Geste | null>(null)
  const dernierToucher = useRef<{ t: number; x: number; y: number } | null>(null)
  const toucherSimple = useRef<number | null>(null)
  const bandeau = useRef<HTMLDivElement>(null)
  // Originaux lus (adresses blob), et proportions des photos à étirer.
  const [originaux, setOriginaux] = useState<Record<string, string>>({})
  const adressesOriginaux = useRef(new Map<string, string>())
  const demandes = useRef(new Set<string>())
  const [proportions, setProportions] = useState<Record<string, number>>({})

  const zoome = vue.s > 1.01
  const aPrecedente = index > 0
  const aSuivante = index < photos.length - 1

  // Nouvelle photo : vue entière.
  useEffect(() => {
    setVue(VUE_ENTIERE)
    setDecalage(0)
    setOriginale(false)
    bandeau.current
      ?.querySelector<HTMLElement>(`[data-rang="${index}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [index])

  useEffect(() => () => {
    if (toucherSimple.current) window.clearTimeout(toucherSimple.current)
    if (finGlissement.current) window.clearTimeout(finGlissement.current)
  }, [])

  /**
   * Fait glisser la piste d'une photo (sens 1 : suivante, -1 : précédente,
   * 0 : retour en place), puis change de photo une fois la course finie.
   */
  function glisserVers(sens: -1 | 0 | 1): void {
    if (finGlissement.current) return
    const largeur = scene.current?.clientWidth ?? 0
    const duree = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : DUREE_GLISSEMENT_MS
    setApparition(false)
    setAnimePiste(duree > 0)
    setDecalage(-sens * (largeur + ECART_PX))
    finGlissement.current = window.setTimeout(() => {
      finGlissement.current = null
      // Même rendu : nouvelle photo au centre, piste remise à zéro, sans saut.
      if (sens !== 0) onIndex(index + sens)
      setAnimePiste(false)
      setDecalage(0)
    }, duree)
  }

  /** Aller à une photo : voisine en glissant, plus loin en fondu. */
  function aller(i: number): void {
    if (i === index || i < 0 || i >= photos.length || finGlissement.current) return
    if (Math.abs(i - index) === 1 && !zoome) {
      glisserVers(i > index ? 1 : -1)
      return
    }
    setApparition(true)
    onIndex(i)
  }
  const allerRef = useRef(aller)
  allerRef.current = aller

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') e.preventDefault()
      if (e.key === 'Escape') {
        if (zoome) setVue(VUE_ENTIERE)
        else onFermer()
      }
      if (e.key === 'ArrowLeft' && aPrecedente) allerRef.current(index - 1)
      if (e.key === 'ArrowRight' && aSuivante) allerRef.current(index + 1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [zoome, aPrecedente, aSuivante, index, onFermer])

  // La photo affichée d'abord, puis ses voisines (le glissement arrive net).
  const idCourante = photo ? (photo.annotation && !originale ? photo.annotation.pj : photo.pj).id : null
  useEffect(() => {
    if (!photo || connexionLente()) return
    const courante = photo.annotation && !originale ? photo.annotation.pj : photo.pj
    const voisines = [photos[index + 1], photos[index - 1]].map((p) => p && (p.annotation?.pj ?? p.pj))
    const file = [courante, ...voisines].filter((m): m is PieceJointe => !!m && m.isImage)
    let annule = false
    void (async () => {
      for (const m of file) {
        if (annule) return
        if (demandes.current.has(m.id)) continue
        demandes.current.add(m.id)
        try {
          const adresse = URL.createObjectURL(await lireContenu(m))
          adressesOriginaux.current.set(m.id, adresse)
          setOriginaux((o) => ({ ...o, [m.id]: adresse }))
        } catch {
          // Pas d'original : l'aperçu reste, il se lit déjà.
        }
      }
    })()
    return () => {
      annule = true
    }
    // La suite dépend de la photo affichée, pas de l'identité du tableau.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCourante, index])

  useEffect(() => {
    const adresses = adressesOriginaux.current
    return () => adresses.forEach((u) => URL.revokeObjectURL(u))
  }, [])

  if (!photo) return null
  const annotation = photo.annotation
  const pj = annotation && !originale ? annotation.pj : photo.pj

  // ─── Géométrie ───

  function taille(): { w: number; h: number; x: number; y: number } {
    const r = scene.current?.getBoundingClientRect()
    return r ? { w: r.width, h: r.height, x: r.left, y: r.top } : { w: 1, h: 1, x: 0, y: 0 }
  }

  /** Position par rapport au centre de la scène. */
  function auCentre(x: number, y: number): { x: number; y: number } {
    const t = taille()
    return { x: x - t.x - t.w / 2, y: y - t.y - t.h / 2 }
  }

  /** Taille de la photo en vue entière (la transformation n'y entre pas). */
  function tailleImage(): { w: number; h: number } {
    const img = scene.current?.querySelector('[data-courant] img') as HTMLImageElement | null | undefined
    return { w: img?.offsetWidth ?? 0, h: img?.offsetHeight ?? 0 }
  }

  /** Zoom borné ; jamais de déplacement au-delà des bords de la photo. */
  function bornerVue(v: Vue): Vue {
    const t = taille()
    const img = tailleImage()
    const s = borner(v.s, 1, ZOOM_MAX)
    const mx = Math.max(0, (s * (img.w || t.w) - t.w) / 2)
    const my = Math.max(0, (s * (img.h || t.h) - t.h) / 2)
    return { s, tx: borner(v.tx, -mx, mx), ty: borner(v.ty, -my, my) }
  }

  /** Zoom vers `s`, le point `p` (rapporté au centre) restant sous le doigt. */
  function zoomerVers(depart: Vue, s: number, p: { x: number; y: number }, arrivee = p): Vue {
    const qx = (p.x - depart.tx) / depart.s
    const qy = (p.y - depart.ty) / depart.s
    return bornerVue({ s, tx: arrivee.x - qx * s, ty: arrivee.y - qy * s })
  }

  // ─── Gestes ───

  function onPointerDown(e: PointerEventReact<HTMLDivElement>): void {
    const cible = e.target as HTMLElement
    // Lecteur vidéo, liens, boutons, lecteur PDF : leurs propres gestes.
    if (cible.closest('video, a, button, [data-pdf]')) return
    // Pendant la course du carrousel : on la laisse finir.
    if (finGlissement.current) return
    scene.current?.setPointerCapture(e.pointerId)
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    setAnime(false)
    const liste = [...pointeurs.current.values()]
    if (liste.length === 1) {
      geste.current = {
        mode: zoome ? 'pan' : 'glisse',
        x0: e.clientX,
        y0: e.clientY,
        vue0: vue,
        dist0: 0,
        milieu0: { x: 0, y: 0 },
        bouge: false,
        surMedia: !!cible.closest('img, [data-media]'),
        t0: Date.now(),
      }
    } else if (liste.length === 2 && pj.isImage) {
      const [a, b] = liste as [{ x: number; y: number }, { x: number; y: number }]
      setDecalage(0)
      geste.current = {
        mode: 'pince',
        x0: 0,
        y0: 0,
        vue0: vue,
        dist0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        milieu0: auCentre((a.x + b.x) / 2, (a.y + b.y) / 2),
        bouge: true,
        surMedia: true,
        t0: Date.now(),
      }
    }
  }

  function onPointerMove(e: PointerEventReact<HTMLDivElement>): void {
    if (!pointeurs.current.has(e.pointerId)) return
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const g = geste.current
    if (!g) return

    if (g.mode === 'pince') {
      const liste = [...pointeurs.current.values()]
      if (liste.length < 2) return
      const [a, b] = liste as [{ x: number; y: number }, { x: number; y: number }]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      const milieu = auCentre((a.x + b.x) / 2, (a.y + b.y) / 2)
      setVue(zoomerVers(g.vue0, borner((g.vue0.s * dist) / g.dist0, 1, ZOOM_MAX), g.milieu0, milieu))
      return
    }

    const dx = e.clientX - g.x0
    const dy = e.clientY - g.y0
    if (!g.bouge && Math.hypot(dx, dy) > SEUIL_MOUVEMENT_PX) g.bouge = true
    if (!g.bouge) return
    if (g.mode === 'pan') setVue(bornerVue({ ...g.vue0, tx: g.vue0.tx + dx, ty: g.vue0.ty + dy }))
    else if (Math.abs(dx) > Math.abs(dy)) {
      // Au bout de la série, la photo résiste.
      const auBout = (dx > 0 && !aPrecedente) || (dx < 0 && !aSuivante)
      setAnimePiste(false)
      setDecalage(auBout ? dx * 0.3 : dx)
    }
  }

  function onPointerUp(e: PointerEventReact<HTMLDivElement>): void {
    if (!pointeurs.current.delete(e.pointerId)) return
    const g = geste.current
    if (!g) return

    // Pincement fini avec un doigt encore posé : il continue en déplacement.
    if (g.mode === 'pince') {
      const reste = [...pointeurs.current.values()][0]
      if (reste) {
        geste.current = { ...g, mode: 'pan', x0: reste.x, y0: reste.y, vue0: vue }
      } else {
        geste.current = null
        if (vue.s < 1.05) {
          setAnime(true)
          setVue(VUE_ENTIERE)
        }
      }
      return
    }
    if (pointeurs.current.size > 0) return
    geste.current = null

    if (g.mode === 'glisse' && g.bouge) {
      const vitesse = decalage / Math.max(1, Date.now() - g.t0)
      const versSuivante = decalage < -SEUIL_GLISSEMENT_PX || (decalage < -20 && vitesse < -VITESSE_LANCER)
      const versPrecedente = decalage > SEUIL_GLISSEMENT_PX || (decalage > 20 && vitesse > VITESSE_LANCER)
      if (versSuivante && aSuivante) glisserVers(1)
      else if (versPrecedente && aPrecedente) glisserVers(-1)
      else glisserVers(0)
      return
    }
    if (g.bouge) return

    // Un toucher : double (zoom) ou simple (fermer, dézoomer), départagés
    // par le délai.
    const maintenant = Date.now()
    const precedent = dernierToucher.current
    if (
      precedent &&
      maintenant - precedent.t < DELAI_DOUBLE_TOUCHE_MS &&
      Math.hypot(e.clientX - precedent.x, e.clientY - precedent.y) < 30
    ) {
      dernierToucher.current = null
      if (toucherSimple.current) window.clearTimeout(toucherSimple.current)
      toucherSimple.current = null
      if (!pj.isImage) return
      setAnime(true)
      setVue(zoome ? VUE_ENTIERE : zoomerVers(VUE_ENTIERE, ZOOM_DOUBLE_TOUCHE, auCentre(e.clientX, e.clientY)))
      return
    }
    dernierToucher.current = { t: maintenant, x: e.clientX, y: e.clientY }
    toucherSimple.current = window.setTimeout(() => {
      toucherSimple.current = null
      if (g.surMedia) {
        setInterfaceVisible((v) => !v)
      } else if (zoome) {
        setAnime(true)
        setVue(VUE_ENTIERE)
      } else {
        onFermer()
      }
    }, DELAI_DOUBLE_TOUCHE_MS)
  }

  function onWheel(e: WheelEvent<HTMLDivElement>): void {
    if (!pj.isImage) return
    setAnime(false)
    const s = borner(vue.s * Math.exp(-e.deltaY * 0.0015), 1, ZOOM_MAX)
    setVue(zoomerVers(vue, s, auCentre(e.clientX, e.clientY)))
  }

  // ─── Rendu ───

  const transformation = `translate(${vue.tx}px, ${vue.ty}px) scale(${vue.s})`
  const commentaire = photo.commentaire
  const avecAnnotation = !!annotation && !originale
  const texteAnnotation = avecAnnotation && annotation.commentaire && !estTexteAutomatique(annotation.commentaire.contenu)
    ? annotation.commentaire.contenu
    : ''
  const texteCommentaire = commentaire && !estTexteAutomatique(commentaire.contenu) ? commentaire.contenu : ''
  const nbProbleme = photos.filter((p) => p.origine === 'probleme').length

  const boutonRond = {
    width: 44,
    height: 44,
    borderRadius: 22,
    border: 'none',
    background: 'rgba(0,0,0,0.45)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as const

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photos du problème"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        backgroundColor: '#000',
        color: 'white',
        display: 'flex',
        flexDirection: pc ? 'row' : 'column',
        userSelect: 'none',
      }}
    >
      {/* Colonne de la photo : en-tête et scène */}
      <div className="flex flex-col" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      {/* En-tête (masqué d'un toucher sur la photo) */}
      {interfaceVisible && (
      <div
        className="flex items-center justify-between"
        style={{ padding: 'calc(4px + env(safe-area-inset-top, 0px)) 8px 4px', flexShrink: 0, position: 'relative', zIndex: 1 }}
      >
        <div className="flex items-center" style={{ gap: 6 }}>
          <button type="button" onClick={onFermer} aria-label="Fermer" style={boutonRond}>
            <X size={24} />
          </button>
          <span style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
            {zoome && pj.isImage ? `× ${vue.s.toFixed(1).replace('.', ',')}` : `${index + 1} / ${photos.length}`}
          </span>
        </div>
        <div className="flex items-center" style={{ gap: 8 }}>
          {/* Annoter : en haut, discret (retour du 09/10 : en bas, sur toute la
              largeur, il prenait la place de la photo sur téléphone). */}
          {onAnnoter && (pj.isImage || pj.isPdf) && !zoome && (
            <button
              type="button"
              onClick={() => onAnnoter(photo, pj)}
              className="flex items-center"
              style={{
                height: 36,
                gap: 6,
                padding: '0 12px',
                borderRadius: 18,
                border: 'none',
                background: 'rgba(255,255,255,0.16)',
                color: 'white',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <PenLine size={16} />
              Annoter
            </button>
          )}
          <a
            href={pj.webUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Télécharger"
            style={{ ...boutonRond, textDecoration: 'none' }}
          >
            <Download size={20} />
          </a>
        </div>
      </div>
      )}

      {/* Scène : gestes */}
      <div
        ref={scene}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        // Le glisser-déposer natif de l'image couperait le geste (pointercancel).
        onDragStart={(e) => e.preventDefault()}
        style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', touchAction: 'none', WebkitTouchCallout: 'none' }}
      >
        {/* Piste du carrousel : précédente, courante, suivante. Chaque photo
            garde son élément (clé) quand elle devient la courante : pas de
            rechargement ni de clignotement à l'arrivée. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transform: `translateX(${decalage}px)`,
            transition: animePiste ? `transform ${DUREE_GLISSEMENT_MS}ms cubic-bezier(0.22, 0.8, 0.3, 1)` : 'none',
            willChange: 'transform',
          }}
        >
          {[-1, 0, 1].map((k) => {
            const p = photos[index + k]
            if (!p) return null
            const courante = k === 0
            const media = courante ? pj : (p.annotation?.pj ?? p.pj)
            return (
              <div
                key={p.pj.id}
                {...(courante ? { 'data-courant': '' } : { 'aria-hidden': true })}
                className={courante && apparition ? 'visionneuse-apparition' : undefined}
                style={{
                  position: 'absolute',
                  inset: 0,
                  transform: `translateX(calc(${k * 100}% + ${k * ECART_PX}px))`,
                  pointerEvents: courante ? 'auto' : 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    containerType: 'size',
                    transform: courante ? transformation : 'none',
                    transformOrigin: 'center',
                    transition: courante && anime ? 'transform 0.22s ease-out' : 'none',
                  }}
                  onTransitionEnd={courante ? () => setAnime(false) : undefined}
                >
                  {media.isImage && (
                    <ImageAvecRepli
                      key={media.id}
                      sources={[media.apercu, media.url]}
                      {...(originaux[media.id] ? { nette: originaux[media.id] } : {})}
                      alt={courante ? media.nom : ''}
                      onLoad={(img) => {
                        // Réduite par l'aperçu : l'original est plus grand, la
                        // photo prend toute la place dès maintenant.
                        if (proportions[media.id] || Math.max(img.naturalWidth, img.naturalHeight) < COTE_APERCU - 2) return
                        const r = img.naturalWidth / img.naturalHeight
                        setProportions((p) => ({ ...p, [media.id]: r }))
                      }}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        pointerEvents: 'auto',
                        // Unités du cadre (containerType ci-dessus) : la plus
                        // grande taille qui tient, aux proportions de la photo.
                        ...(proportions[media.id]
                          ? { width: `min(100cqw, ${(100 * (proportions[media.id] ?? 1)).toFixed(3)}cqh)`, height: 'auto' }
                          : {}),
                      }}
                    />
                  )}
                  {media.isVideo && courante && (
                    <video
                      key={media.id}
                      src={media.url}
                      controls
                      playsInline
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  )}
                  {!media.isImage && !courante && <FileText size={64} color="rgba(255,255,255,0.4)" />}
                  {media.isPdf && courante && (
                    <LecteurPdf key={media.id} pj={media} repli={<CarteFichier pj={media} />} margeHaut={annotation ? 46 : 8} />
                  )}
                  {!media.isImage && !media.isVideo && !media.isPdf && courante && <CarteFichier pj={media} />}
                </div>
              </div>
            )
          })}
        </div>

        {/* Photo annotée : sélecteur posé SUR la photo, pas sur une ligne à lui */}
        {annotation && !zoome && interfaceVisible && (
          <div
            role="group"
            aria-label="Version affichée"
            className="flex"
            style={{
              position: 'absolute',
              top: 6,
              left: '50%',
              transform: 'translateX(-50%)',
              gap: 2,
              background: 'rgba(0,0,0,0.55)',
              borderRadius: 17,
              padding: 2,
            }}
          >
            {[
              { libelle: 'Annotée', actif: !originale, choisir: () => setOriginale(false) },
              { libelle: 'Originale', actif: originale, choisir: () => setOriginale(true) },
            ].map((b) => (
              <button
                key={b.libelle}
                type="button"
                aria-pressed={b.actif}
                onClick={b.choisir}
                style={{
                  height: 30,
                  padding: '0 12px',
                  border: 'none',
                  borderRadius: 15,
                  fontSize: 13,
                  fontWeight: 500,
                  background: b.actif ? 'white' : 'transparent',
                  color: b.actif ? 'black' : 'white',
                }}
              >
                {b.libelle}
              </button>
            ))}
          </div>
        )}

        {/* PC : flèches sur les côtés */}
        {!zoome && aPrecedente && (
          <button
            type="button"
            onClick={() => aller(index - 1)}
            aria-label="Photo précédente"
            className="visionneuse-fleche"
            style={{ ...boutonRond, position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronLeft size={24} />
          </button>
        )}
        {!zoome && aSuivante && (
          <button
            type="button"
            onClick={() => aller(index + 1)}
            aria-label="Photo suivante"
            className="visionneuse-fleche"
            style={{ ...boutonRond, position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronRight size={24} />
          </button>
        )}

        {/* Zoom : la partie agrandie, et comment revenir */}
        {zoome && pj.isImage && (
          <>
            <MiniVue
              sources={[pj.vignette, pj.apercu, pj.url]}
              vue={vue}
              scene={taille()}
              image={tailleImage()}
            />
            <span
              style={{
                position: 'absolute',
                left: '50%',
                bottom: 16,
                transform: 'translateX(-50%)',
                padding: '10px 16px',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.6)',
                fontSize: 14,
                color: 'rgba(255,255,255,0.85)',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              {pc ? 'Double-clic pour revenir à la photo entière' : 'Touchez deux fois pour revenir à la photo entière'}
            </span>
          </>
        )}
      </div>

      </div>

      {/* Légende et vignettes. Téléphone : dessous, au plus serré (retour du
          09/10), masquées pendant le zoom. PC : panneau à droite. Masquées
          d'un toucher sur la photo. */}
      {(!zoome || pc) && interfaceVisible && (
        <div
          className="flex flex-col"
          style={pc
            ? {
                width: 340,
                flexShrink: 0,
                gap: 12,
                padding: '20px 20px 24px',
                overflowY: 'auto',
                boxSizing: 'border-box',
                background: '#0d0d0d',
                borderLeft: '1px solid rgba(255,255,255,0.1)',
              }
            : {
                flexShrink: 0,
                gap: 8,
                padding: '8px 12px calc(8px + env(safe-area-inset-bottom, 0px))',
                width: '100%',
                maxWidth: 720,
                marginInline: 'auto',
                boxSizing: 'border-box',
              }}
        >
          {/* Qui, quand : une ligne */}
          <div className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
            {photo.origine === 'activite' ? (
              <PastillePersonne
                nom={photo.auteur}
                upn={photo.auteurUpn}
                taille={24}
                fond="var(--color-teal)"
                couleur="var(--color-black)"
                taillePolice={10}
              />
            ) : (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 11,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  color: 'var(--color-black)',
                  background: 'white',
                  borderRadius: 999,
                  padding: '2px 8px',
                }}
              >
                Problème
              </span>
            )}
            <span style={{ ...uneLigne, flex: 1, fontSize: 14 }}>
              <span style={{ fontWeight: 500 }}>{photo.auteur || 'Activité'}</span>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>{photo.date ? ` · ${quand(photo.date)}` : ''}</span>
            </span>
            {commentaire && onVoirDansActivite && <LienActivite onClick={() => onVoirDansActivite(commentaire.id)} />}
          </div>
          {/* PDF ou autre fichier : son nom d'origine, pour savoir de quoi on parle */}
          {!pj.isImage && (
            <div className="flex items-start" style={{ gap: 8, minWidth: 0 }}>
              <FileText
                size={16}
                aria-hidden="true"
                style={{ flexShrink: 0, marginInline: 4, marginTop: 2, color: pj.isPdf ? 'var(--color-traffic-red)' : 'rgba(255,255,255,0.75)' }}
              />
              <span style={{ ...texteCourt, flex: 1, fontWeight: 500, WebkitLineClamp: pc ? 4 : 2 }} title={nomAffiche(pj.nom)}>
                {nomAffiche(pj.nom)}
              </span>
            </div>
          )}
          {texteCommentaire && <p style={{ ...texteCourt, WebkitLineClamp: pc ? 14 : 3 }}>{texteCommentaire}</p>}

          {/* Annotée : une ligne aussi */}
          {avecAnnotation && (
            <>
              <div className="flex items-center" style={{ gap: 8, minWidth: 0, color: 'rgba(255,255,255,0.75)' }}>
                <PenLine size={14} aria-hidden="true" style={{ flexShrink: 0, marginInline: 5 }} />
                <span style={{ ...uneLigne, flex: 1, fontSize: 13 }}>
                  Annotée par {annotation.auteur || 'un collègue'}{annotation.date ? ` · ${quand(annotation.date)}` : ''}
                </span>
                {annotation.commentaire && onVoirDansActivite && (
                  <LienActivite onClick={() => onVoirDansActivite(annotation.commentaire!.id)} />
                )}
              </div>
              {texteAnnotation && <p style={{ ...texteCourt, WebkitLineClamp: pc ? 8 : 2 }}>« {texteAnnotation} »</p>}
            </>
          )}

          {/* PC : toutes les vignettes, en deux groupes. */}
          {photos.length > 1 && pc && (
            <div ref={bandeau} className="flex flex-col" style={{ gap: 14, marginTop: 8 }}>
              {[
                { titre: 'Déclaration', debut: 0, fin: nbProbleme },
                { titre: 'Activité', debut: nbProbleme, fin: photos.length },
              ].filter((g) => g.fin > g.debut).map((g) => (
                <div key={g.titre} className="flex flex-col" style={{ gap: 8 }}>
                  <span style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 500, color: 'rgba(255,255,255,0.55)' }}>
                    {g.titre}
                  </span>
                  <div className="flex flex-wrap" style={{ gap: 8, padding: 2 }}>
                    {photos.slice(g.debut, g.fin).map((p, k) => {
                      const i = g.debut + k
                      return (
                        <span
                          key={p.pj.id}
                          data-rang={i}
                          style={{
                            display: 'block',
                            borderRadius: 8,
                            outline: i === index ? '2px solid var(--color-yellow)' : 'none',
                            outlineOffset: 2,
                            opacity: i === index ? 1 : 0.6,
                          }}
                        >
                          <Vignette
                            pj={p.annotation?.pj ?? p.pj}
                            size={66}
                            rayon={8}
                            onClick={() => aller(i)}
                            libelle={`${p.origine === 'probleme' ? 'Photo du problème' : 'Photo de l’activité'} ${i + 1}`}
                          />
                        </span>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {photos.length > 1 && !pc && (
            <div ref={bandeau} className="flex items-center" style={{ gap: 6, overflowX: 'auto', padding: '3px 2px' }}>
              {photos.map((p, i) => (
                <span key={p.pj.id} data-rang={i} className="flex items-center" style={{ gap: 6, flexShrink: 0 }}>
                  {i === nbProbleme && nbProbleme > 0 && (
                    <span aria-hidden="true" style={{ width: 1, height: 30, background: 'rgba(255,255,255,0.3)', margin: '0 4px' }} />
                  )}
                  <span
                    style={{
                      display: 'block',
                      borderRadius: 7,
                      outline: i === index ? '2px solid var(--color-yellow)' : 'none',
                      outlineOffset: 2,
                      opacity: i === index ? 1 : 0.55,
                    }}
                  >
                    <Vignette
                      pj={p.annotation?.pj ?? p.pj}
                      size={44}
                      rayon={7}
                      onClick={() => aller(i)}
                      libelle={`${p.origine === 'probleme' ? 'Photo du problème' : 'Photo de l’activité'} ${i + 1}`}
                    />
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Fichier qui ne s'affiche pas ici (ou PDF illisible) : son nom et un lien vers SharePoint. */
function CarteFichier({ pj }: { pj: PieceJointe }): JSX.Element {
  return (
    <div data-media className="flex flex-col items-center gap-4" style={{ textAlign: 'center', padding: 24 }}>
      <FileText size={64} color="rgba(255,255,255,0.6)" />
      <p style={{ fontSize: 'var(--font-size-copy2)', opacity: 0.8, margin: 0 }}>{nomAffiche(pj.nom)}</p>
      <a
        href={pj.webUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 px-4 py-2 rounded"
        style={{
          backgroundColor: 'var(--color-yellow)',
          color: 'var(--color-black)',
          fontSize: 'var(--font-size-copy2)',
          fontWeight: 500,
          textDecoration: 'none',
        }}
      >
        <ExternalLink size={16} />
        Ouvrir dans SharePoint
      </a>
    </div>
  )
}

/** Coin bas droit pendant le zoom : la photo entière, la partie affichée encadrée. */
function MiniVue({
  sources,
  vue,
  scene,
  image,
}: {
  sources: (string | undefined)[]
  vue: Vue
  scene: { w: number; h: number }
  /** Taille de la photo affichée en vue entière. */
  image: { w: number; h: number }
}): JSX.Element | null {
  if (image.w <= 0 || image.h <= 0) return null
  // Partie visible, en fractions de la photo (repère : son coin haut gauche).
  const cx = 0.5 - vue.tx / (vue.s * image.w)
  const cy = 0.5 - vue.ty / (vue.s * image.h)
  const demiL = scene.w / (2 * vue.s * image.w)
  const demiH = scene.h / (2 * vue.s * image.h)
  const gauche = borner(cx - demiL, 0, 1)
  const haut = borner(cy - demiH, 0, 1)
  const droite = borner(cx + demiL, 0, 1)
  const bas = borner(cy + demiH, 0, 1)
  // Boîte aux proportions de la photo, dans 96 × 72.
  const ratio = image.w / image.h
  const largeur = ratio >= 96 / 72 ? 96 : Math.round(72 * ratio)
  const hauteur = ratio >= 96 / 72 ? Math.round(96 / ratio) : 72
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        right: 16,
        bottom: 72,
        width: largeur,
        height: hauteur,
        borderRadius: 6,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.5)',
        background: '#222',
        pointerEvents: 'none',
      }}
    >
      <ImageAvecRepli sources={sources} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      <span
        style={{
          position: 'absolute',
          left: `${gauche * 100}%`,
          top: `${haut * 100}%`,
          width: `${(droite - gauche) * 100}%`,
          height: `${(bas - haut) * 100}%`,
          border: '2px solid var(--color-yellow)',
          boxSizing: 'border-box',
        }}
      />
    </div>
  )
}

const uneLigne = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } as const

const texteCourt = {
  margin: 0,
  fontSize: 14,
  lineHeight: '19px',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const

/** « Voir › » : l'événement dans l'activité. */
function LienActivite({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Voir dans l’activité"
      className="flex items-center"
      style={{
        flexShrink: 0,
        gap: 1,
        minHeight: 32,
        padding: '0 2px 0 8px',
        background: 'none',
        border: 'none',
        color: 'white',
        fontSize: 14,
        fontWeight: 500,
      }}
    >
      Voir
      <ChevronRight size={16} />
    </button>
  )
}
